/**
 * Fluent API Interface
 *
 * Provides a more intuitive and chainable API for making requests.
 * Eliminates the need to remember service names as parameters.
 *
 * @example
 * // Simple request
 * const tickers = await api.bff.get<RawTicker[]>('/v1/tickers');
 *
 * @example
 * // With normalization
 * const tickers = await api.bff
 *   .get<RawTicker[]>('/v1/tickers')
 *   .normalize(normalizeTickers);
 *
 * @example
 * // Silent mode - no default error toast
 * const tickers = await api.bff.get<RawTicker[]>('/v1/tickers', {
 *   silent: true,
 *   onError: (error) => console.error('Failed:', error)
 * });
 *
 * @example
 * // With success callback
 * const result = await api.oex.post<OrderResult>('/v1/orders', orderData, {
 *   onSuccess: (data) => console.log('Order placed:', data)
 * });
 */

import { ApiError, getApiClient, type BackendService } from './client';
import type { ApiResponse } from './types';

/**
 * Generate cache key from URL and query parameters
 * This is used for React Query queryKey to ensure different params create different cache entries
 *
 * @param url - Request URL
 * @param params - Query parameters
 * @returns Array suitable for React Query queryKey
 *
 * @example
 * // Without params
 * getCacheKey('/v1/tickers') // ['/v1/tickers']
 *
 * @example
 * // With params
 * getCacheKey('/v1/sparklines', { instrumentType: 'CCY_PAIR' })
 * // ['/v1/sparklines', { instrumentType: 'CCY_PAIR' }]
 */
export function getCacheKey(
  url: string,
  params?: Record<string, any>,
): readonly [string] | readonly [string, Record<string, any>] {
  if (!params || Object.keys(params).length === 0) {
    return [url] as const;
  }

  // Filter out undefined/null values
  const cleanParams: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      cleanParams[key] = value;
    }
  }

  return [url, cleanParams] as const;
}

/**
 * API Request builder with chainable methods
 *
 * This class implements a thenable interface to allow direct await usage.
 * The ESLint warning is suppressed because this is an intentional design
 * for API request chaining.
 */
export class ApiRequest<T> {
  private readonly promise: Promise<T>;

  constructor(promise: Promise<T>) {
    this.promise = promise;
  }

  /**
   * Transform the response data using a normalization function
   *
   * @param normalizer - Function to transform the data
   * @returns New ApiRequest with transformed data
   *
   * @example
   * const tickers = await api.bff
   *   .get<RawTicker[]>('/v1/tickers')
   *   .normalize(normalizeTickers);
   */
  normalize<R>(normalizer: (data: T) => R): ApiRequest<R> {
    return new ApiRequest(this.promise.then(normalizer));
  }

  /**
   * Convert to Promise (allows using await)
   * This method makes the object awaitable.
   *
   * Note: ESLint warning about thenable is suppressed because this is
   * an intentional design for API request chaining.
   *
   * @returns Promise that resolves to the data
   */
  // eslint-disable-next-line unicorn/no-thenable
  then<TResult1 = T, TResult2 = never>(
    onfulfilled?:
      | ((value: T) => TResult1 | PromiseLike<TResult1>)
      | null
      | undefined,
    onrejected?:
      | ((reason: any) => TResult2 | PromiseLike<TResult2>)
      | null
      | undefined,
  ): Promise<TResult1 | TResult2> {
    return this.promise.then(onfulfilled, onrejected);
  }
}

/**
 * Service-specific API methods
 */
export class ServiceApi {
  constructor(private readonly service: BackendService) {}

  /**
   * Make a GET request
   *
   * @param url - Request URL
   * @param config - Request configuration (including silent, onError, onSuccess)
   * @returns ApiRequest that can be chained or awaited
   *
   * @example
   * const tickers = await api.bff.get<RawTicker[]>('/v1/tickers');
   *
   * @example
   * const tickers = await api.bff
   *   .get<RawTicker[]>('/v1/tickers')
   *   .normalize(normalizeTickers);
   *
   * @example
   * const tickers = await api.bff.get<RawTicker[]>('/v1/tickers', {
   *   silent: true,
   *   onError: (error) => console.error('Failed:', error)
   * });
   */
  get<T>(
    url: string,
    config?: Parameters<ReturnType<typeof getApiClient>['get']>[1],
  ): ApiRequest<T> {
    const promise = (async () => {
      const client = getApiClient(this.service);
      const response = await client.get<T>(url, config);

      if (response.code !== 0) {
        const error = new ApiError(
          response.code,
          response.message || 'Request failed',
          response.data,
          response.httpStatus,
        );

        // Call custom error handler if provided
        if (config?.onError) {
          config.onError(error);
        }

        // In silent mode, don't throw error
        if (config?.silent) {
          return undefined as T;
        }

        throw error;
      }

      // Call success handler if provided
      if (config?.onSuccess) {
        config.onSuccess(response.data as T);
      }

      return response.data as T;
    })();

    return new ApiRequest(promise);
  }

  /**
   * Make a POST request
   *
   * @param url - Request URL
   * @param body - Request body
   * @param config - Request configuration (including silent, onError, onSuccess)
   * @returns ApiRequest that can be chained or awaited
   *
   * @example
   * const result = await api.oex.post<OrderResult>('/v1/orders', {
   *   symbol: 'BTC_USD',
   *   qty: 1
   * });
   *
   * @example
   * const result = await api.oex.post<OrderResult>('/v1/orders', orderData, {
   *   silent: true,
   *   onSuccess: (data) => console.log('Order placed:', data)
   * });
   */
  post<T>(
    url: string,
    body?: any,
    config?: Parameters<ReturnType<typeof getApiClient>['post']>[2],
  ): ApiRequest<T> {
    const promise = (async () => {
      const client = getApiClient(this.service);
      const response = await client.post<T>(url, body, config);

      if (response.code !== 0) {
        const error = new ApiError(
          response.code,
          response.message || 'Request failed',
          response.data,
          response.httpStatus,
        );

        // Call custom error handler if provided
        if (config?.onError) {
          config.onError(error);
        }

        // In silent mode, don't throw error
        if (config?.silent) {
          return undefined as T;
        }

        throw error;
      }

      // Call success handler if provided
      if (config?.onSuccess) {
        config.onSuccess(response.data as T);
      }

      return response.data as T;
    })();

    return new ApiRequest(promise);
  }

  /**
   * Make a GET request without throwing on error
   * Returns null if request fails
   *
   * @param url - Request URL
   * @param config - Request configuration
   * @returns ApiRequest with data or null
   *
   * @example
   * const tickers = await api.bff.getSafe<RawTicker[]>('/v1/tickers');
   * if (tickers) {
   *   // Handle success
   * }
   */
  getSafe<T>(
    url: string,
    config?: Parameters<ReturnType<typeof getApiClient>['get']>[1],
  ): ApiRequest<T | null> {
    const promise = (async () => {
      try {
        return await this.get<T>(url, config);
      } catch {
        return null;
      }
    })();

    return new ApiRequest(promise);
  }

  /**
   * Make a POST request without throwing on error
   * Returns null if request fails
   *
   * @param url - Request URL
   * @param body - Request body
   * @param config - Request configuration
   * @returns ApiRequest with data or null
   *
   * @example
   * const result = await api.oex.postSafe<OrderResult>('/v1/orders', {
   *   symbol: 'BTC_USD',
   *   qty: 1
   * });
   * if (result) {
   *   // Handle success
   * }
   */
  postSafe<T>(
    url: string,
    body?: any,
    config?: Parameters<ReturnType<typeof getApiClient>['post']>[2],
  ): ApiRequest<T | null> {
    const promise = (async () => {
      try {
        return await this.post<T>(url, body, config);
      } catch {
        return null;
      }
    })();

    return new ApiRequest(promise);
  }

  /**
   * Get full API response (including code, message, data)
   * Useful when you need to handle different response codes
   *
   * @param url - Request URL
   * @param config - Request configuration
   * @returns Promise with full API response
   *
   * @example
   * const response = await api.bff.getResponse<RawTicker[]>('/v1/tickers');
   * if (response.code === 0) {
   *   // Handle success
   * } else {
   *   // Handle specific error codes
   * }
   */
  async getResponse<T>(
    url: string,
    config?: Parameters<ReturnType<typeof getApiClient>['get']>[1],
  ): Promise<ApiResponse<T>> {
    const client = getApiClient(this.service);
    return client.get<T>(url, config);
  }

  /**
   * Get full API response for POST request
   *
   * @param url - Request URL
   * @param body - Request body
   * @param config - Request configuration
   * @returns Promise with full API response
   *
   * @example
   * const response = await api.oex.postResponse<OrderResult>('/v1/orders', {
   *   symbol: 'BTC_USD',
   *   qty: 1
   * });
   * if (response.code === 0) {
   *   // Handle success
   * } else {
   *   // Handle specific error codes
   * }
   */
  async postResponse<T>(
    url: string,
    body?: any,
    config?: Parameters<ReturnType<typeof getApiClient>['post']>[2],
  ): Promise<ApiResponse<T>> {
    const client = getApiClient(this.service);
    return client.post<T>(url, body, config);
  }
}

/**
 * Fluent API object
 * Provides service-specific API methods
 *
 * @example
 * // GET request
 * const tickers = await api.bff.get<RawTicker[]>('/v1/tickers');
 *
 * @example
 * // With normalization
 * const tickers = await api.bff
 *   .get<RawTicker[]>('/v1/tickers')
 *   .normalize(normalizeTickers);
 *
 * @example
 * // POST request
 * const result = await api.oex.post<OrderResult>('/v1/orders', {
 *   symbol: 'BTC_USD',
 *   qty: 1
 * });
 */
export const api = {
  /** BFF service API */
  bff: new ServiceApi('bff'),
  /** OEX service API */
  oex: new ServiceApi('oex'),
  /** NTS service API */
  nts: new ServiceApi('nts'),
  /** BE service API */
  be: new ServiceApi('be'),
  /** Worker service API */
  worker: new ServiceApi('worker'),
} as const;
